import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { mapGetters } from 'vuex';
import * as msTeams from '@microsoft/teams-js';

import * as accountTypes from '@account/store/types';
import { cache } from '@appbase/util';
import api from '@/api';
import { CardOpenMethod } from './UserCardComponent';

@Component({
    computed: {
        ...mapGetters({
            currentUser: accountTypes.USER,
        }),
    },
})
export default class UserCard extends Vue {
    @Prop({ type: Number }) openMethod!: CardOpenMethod
    @Prop({ type: String, default: '/hr/Person/api/detail' }) identityApiUrl!: string

    // ──────────────────────────────────────────────────────── I ──────────
    //   :::::: C O M P U T E D : :  :   :    :     :        :          :
    // ──────────────────────────────────────────────────────────────────
    //
    get chatUrl() {
        if(this.targetUser == null) {
            return '';
        } else {
            return `https://teams.microsoft.com/l/chat/0/0?users=${this.targetUser.userPrincipalName}&topicName=&message=`;
        }
            
        
    }

    get meetingUrl() {
        if(this.targetUser == null) {
            return '';
        } else {
            return `https://teams.microsoft.com/l/meeting/new?subject=&startTime=&endTime=&content=&attendees=${this.targetUser.userPrincipalName}`;
        }        
    }

    get status() {
        if(this.targetUser == null)
            return '';

        var status = '';
        switch (this.targetUser.availability) {
            case 'Available':
            case 'AvailableIdle':
                status = '대화가능';
                break;
            case 'Away':
            case 'BeRightBack':
                status = '자리비움';
                break;
            case 'Busy':
            case 'BusyIdle':
            case 'DoNotDisturb':
                status = '다른용무중';
                break;
            case 'Offline':
                status = '오프라인';
                break;
            case 'PresenceUnknown':
            default:
                status = '알수없음';
                break;
        }
        return status;
    }

    get closeImage() {
        return require(`@appbase/assets/${this.closeStatus}/card_close.png`);
    }

    get chatImage() {
        return require(`@appbase/assets/${this.chatStatus}/mark_chat_read.png`);
    }
    
    get meetImage() {
        return require(`@appbase/assets/${this.meetStatus}/calendar_month.png`);
    }

    get mailImage() {
        return require(`@appbase/assets/${this.mailStatus}/mail_outline.png`);
    }

    //
    // ──────────────────────────────────────────────── I ──────────
    //   :::::: G E T T E R S : :  :   :    :     :        :          :
    // ──────────────────────────────────────────────────────────
    //
    currentUser!: account.User;

    //
    // ──────────────────────────────────────────────── I ──────────
    //   :::::: D A T A : :  :   :    :     :        :          :
    // ──────────────────────────────────────────────────────────
    //
    targetUser: appbase.account.UserDetail | null = null;
    targetPosition: DOMRect = Object.create(null);
    currentElem: HTMLElement | null = null;  
    photoUrl: string = '';

    isOpen: boolean = false;
    isLoading: boolean = true;
    isPhotoLoading: boolean = true;
    isFocusing: boolean = false;

    styleTop: string = '0';
    styleLeft: string = '0';

    closeTimeout: number | null = null;

    ghostImage: any = require('@appbase/assets/ghost.png');
    ghostLoading: any = require('@appbase/assets/ghost_loading.png');

    closeStatus: string = 'out';
    chatStatus: string = 'out';
    meetStatus: string = 'out';
    mailStatus: string = 'out';

    //
    // ────────────────────────────────────────────────────── I ──────────
    //   :::::: M E T H O D S : :  :   :    :     :        :          :
    // ────────────────────────────────────────────────────────────────
    //
    openChat() {
        if(this.targetUser == null)
            return;

        if (this.currentUser.personCode == this.targetUser.personCode) {
            let message: string = this.$t('자신과의_채팅_불가').toString();
            if (this.isTeams) message = message + `\n(${this.$t('우측상단_본인사진_더블클릭').toString()})`;
            (window as any).alertAsync(message);
            return;
        }

        if (this.isTeams) {
            if (this.targetUser.isTeamsUser) {
                msTeams.chat.openChat({
                    user: `${this.targetUser.userPrincipalName}` ?? '',
                });
            } else {
                (window as any).alertAsync(this.$t('팀즈_미사용_사용자').toString());
            }
        } else {
            window.open(this.chatUrl);
        }
    }

    openMeeting() {
        if(this.targetUser == null)
            return;

        if (this.isTeams) {
            if (this.targetUser.isTeamsUser) {
                msTeams.app.openLink(this.meetingUrl);
            } else {
                (window as any).alertAsync(this.$t('팀즈_미사용_사용자').toString());
            }
        } else {
            window.open(this.meetingUrl);
        }
    }

    sendMail() {
        if(this.targetUser == null)
            return;

        const uri = `mailto:${this.targetUser.email}`;
        window.open(uri);
    }

    @Watch('targetUser')
    getPhotoPath() {
        if(this.targetUser == null)
            return;

        this.isPhotoLoading = true;
        if (this.targetUser.photoPath) {
            this.photoUrl = this.targetUser.photoPath;
        } else {
            this.photoUrl = '/cm/gres/faux/images/ghost.png';
        }
        this.isPhotoLoading = false;
    }

    async setTargetUser(userData: string, type: 'personCode' | 'upn' | 'email') {
        let baseUrl = `${this.identityApiUrl}?`;

        if (type === 'personCode') {
            baseUrl += `personCode=${userData}`;
        } else if (type === 'upn') {
            baseUrl += `upn=${userData}`;
        } else if (type === 'email') {
            baseUrl += `email=${userData}`;
        } else {
            this.targetUser = null;
            return;
        }

        const result: appbase.account.UserDetail | null = await api.get({
          url: baseUrl,
        });

        this.targetUser = result;
    }

    handleOpen(openElem: HTMLElement, userData: string, type: 'personCode' | 'upn' | 'email') {          
        if (!userData) return;                   

        if(this.currentElem == openElem) 
            return;

        let elRect = openElem.getBoundingClientRect();        

        let posTop = elRect.y;
        let posLeft = elRect.x;   
    
        let topDiff = (posTop + 400) - document.body.clientHeight;
        let leftDiff = (posLeft + 336) - document.body.clientWidth;

        if(topDiff > 0) {
            posTop-= topDiff;
        }

        if(leftDiff > 0) {
            posLeft-=leftDiff;
        }
        
        this.styleTop = `${posTop}px`;
        this.styleLeft = `${posLeft}px`;                

        this.setTargetUser(userData, type).then(() => {
            if(this.targetUser != null) {
                this.isOpen = true;
            }            
        });
    }

    handleClose(force: boolean) {
        if(force) {
            this.isOpen = false    
        } else {        
            this.closeTimeout = setTimeout(() => {
                this.isOpen = false   
            }, 500);                                            
        }        
    }

    handleMouseEnter(e: MouseEvent) {
        this.isFocusing = true;
        if(this.closeTimeout) {
            clearTimeout(this.closeTimeout);
        }        
    }

    handleMouseLeave(e: MouseEvent) {
        if(this.openMethod === CardOpenMethod.MouseOver) {
            this.isFocusing = false;
            this.handleClose(false);
        }        
    }

    onClickClose() {
        this.handleClose(true);
    }
}
